import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useUserSession } from "../../hooks/useUserSession";
import { useQuery } from "react-query";
import { UserAccountsService } from "../../gen/clients/llts";

/**
 * LocaleManager component handles automatic locale switching based on the LLTS branch ID.
 *
 * When the branch ID is "uk" (case-insensitive), the locale is automatically switched to British English (en-GB).
 * Otherwise, the default American English (en) locale is used.
 *
 * This component renders nothing and only performs side effects.
 */
const LocaleManager: React.FC = () => {
  const { i18n } = useTranslation();
  const { username } = useUserSession();
  const location = useLocation();

  const { data: userBranch } = useQuery(["getCurrentUserBranch", username], {
    queryFn: UserAccountsService.getCurrentUserBranch,
    enabled: !!username
  });

  useEffect(() => {
    // Extract the branch ID from the URL path
    // The path format is /:lltsBranchId?/... where lltsBranchId is optional
    const pathParts = location.pathname.split("/").filter(part => part.length > 0);

    // The first part of the path could be the branch ID
    // We need to check if it's "uk" (case-insensitive)
    const potentialBranchId = pathParts[0];

    // Determine the appropriate locale based on the branch ID from the URL
    let targetLocale: "en-GB" | "en";
    switch (potentialBranchId?.toLowerCase() || userBranch?.id) {
      case "uk":
      case "en-gb":
      case "en-GB":
        targetLocale = "en-GB";
        break;
      default:
        targetLocale = "en";
        break;
    }

    // Only change language if it's different from the current language
    if (i18n.language !== targetLocale) {
      i18n.changeLanguage(targetLocale);
    }
  }, [location.pathname, i18n, userBranch?.id]);

  // This component doesn't render anything
  return null;
};

export default LocaleManager;
